import React, { useContext } from 'react';
import Icon from '@common/assets';
import Text from '@common/text';
import { useChatToast } from '@components/chat/common/chatToast';
import { ChatContext, ChatContextType } from '@components/chat/ChatContext';
import useChatNotification from '@components/chat/hooks/useChatNotification';
import { ChatMenu } from '@domain/constant/ChatMenuType';
import { FontStyle } from '@domain/constant/fontStyle';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface ChatHeaderProps {
  handleCloseChatSNB: () => void;
}

const ChatHeader = ({ handleCloseChatSNB }: ChatHeaderProps) => {
  const { chatMenu, setChatMenu, channelInfo, isNotifying, setIsNotifying } = useContext<ChatContextType>(ChatContext);
  const { onNotification, offNotification } = useChatNotification();
  const ChatToast = useChatToast();
  const name = channelInfo?.name;

  const getMemberCount = () => {
    const count = channelInfo?.memberCount || 0;
    return count > 0 ? count : 0;
  };

  const handleClose = () => handleCloseChatSNB();
  const handleSearch = () => setChatMenu(ChatMenu.SEARCH);
  const handleBack = () => setChatMenu(ChatMenu.LIST);

  const handleClickOn = async () => {
    if (!channelInfo) return;
    await onNotification({
      channelId: channelInfo.id,
      callback: () => {
        setIsNotifying(true);
        ChatToast({
          bottom: 72,
          content: (
            <div>
              <Text element={'span'}>알림이 켜졌어요.</Text>
              <br />
              <Text element={'span'} fontStyle={FontStyle.B4_M}>
                이제 모바일 앱을 통해 새 메시지, 공고를 실시간으로 받아보세요.
              </Text>
            </div>
          ),
        });
      },
    });
  };

  const handleClickOff = async () => {
    if (!channelInfo) return;
    await offNotification({
      channelId: channelInfo.id,
      callback: () => {
        setIsNotifying(false);
        ChatToast({
          bottom: 72,
          content: (
            <div>
              <Text element={'span'}>알림이 꺼졌어요.</Text>
              <br />
              <Text element={'span'} fontStyle={FontStyle.B4_M}>
                새 메시지, 공고를 쉽게 확인하고 싶다면 알림을 켜주세요.
              </Text>
            </div>
          ),
        });
      },
    });
  };

  switch (chatMenu) {
    case ChatMenu.LIST:
      return (
        <div className={cx('headerArea')}>
          <button role={'button'} aria-label="close" className={cx('btn', 'left')} onClick={handleClose}>
            <Icon name="closeLight" width={32} height={32} />
          </button>
          <div className={cx('titleArea')}>
            <span className={cx('title')}>채팅</span>
          </div>
          <button role={'button'} aria-label="search" className={cx('btn', 'right')} onClick={handleSearch}>
            <Icon name="searchLight" width={32} height={32} />
          </button>
        </div>
      );
    case ChatMenu.SEARCH:
      return (
        <div className={cx('headerArea')}>
          <button role={'button'} aria-label="back" className={cx('btn', 'left')} onClick={handleBack}>
            <Icon name="largeArrowLeftLight" width={32} height={32} />
          </button>
          <div className={cx('titleArea')}>
            <span className={cx('title')}>검색</span>
          </div>
        </div>
      );
    case ChatMenu.DETAIL:
      return (
        <div className={cx('headerArea')}>
          <button role={'button'} aria-label="back" className={cx('btn', 'left')} onClick={handleBack}>
            <Icon name="largeArrowLeftLight" width={32} height={32} />
          </button>
          <div className={cx('titleArea')}>
            <span className={cx('title')} title={name}>
              {name}
            </span>
            <span className={cx('number')}>({getMemberCount()}명)</span>
          </div>
          {isNotifying ? (
            <button role={'button'} aria-label="onNotification" className={cx('btn', 'right')} onClick={handleClickOff}>
              <Icon name="bellLight" width={32} height={32} />
            </button>
          ) : (
            <button role={'button'} aria-label="offNotification" className={cx('btn', 'right')} onClick={handleClickOn}>
              <Icon name="bellOffLight" width={32} height={32} />
            </button>
          )}
        </div>
      );
    default:
      return null;
  }
};

export default ChatHeader;
